import { FORM_LABELS } from '@/constants';
import { Select } from '@/components/Fields';
import { TextContent } from '@/components/Text';
import { IManagerProduct, IManagerProductOption } from '@/types';
import React, { useMemo } from 'react';

// Extracted constants for better readability
const { PRODUCT_CHANGE, PRODUCT_SELECT, SELECT_CERTIFICATION } = FORM_LABELS;

/** Props for the ManagerDashboardProductSection component */
export interface ManagerDashboardProductSectionProps {
  /** The currently selected certification product */
  activeProduct: IManagerProduct | null;
  /** Loading state for the dashboard */
  isLoading: boolean;
  /** Callback when the product dropdown changes */
  onProductChange: (value: string) => void;
  /** Options for the product dropdown */
  productOptions: IManagerProductOption[];
}

/**
 * ManagerDashboardProductSection Component
 *
 * Renders the product title and the certification selector for the manager dashboard.
 */
export const ManagerDashboardProductSection: React.FC<ManagerDashboardProductSectionProps> = ({
  activeProduct,
  isLoading,
  onProductChange,
  productOptions,
}) => {
  /**
   * Memoized values for the product section title, dropdown placeholder, and dropdown value.
   * These values are derived from the activeProduct and are recalculated only when activeProduct changes.
   */
  const [title, placeholder, value] = useMemo(() => {
    const placeholderText = activeProduct ? PRODUCT_CHANGE : PRODUCT_SELECT;
    const valueText = activeProduct ? activeProduct.certificationId.toString() : undefined;
    const titleText = activeProduct ? activeProduct.certificationTitle : SELECT_CERTIFICATION;
    return [titleText, placeholderText, valueText];
  }, [activeProduct]);

  // ===============================
  // Render
  // ================================
  return (
    <div className="flex items-center space-x-4">
      <TextContent
        isLoading={isLoading}
        level={2}
        type="headline"
        variant="sectionTitle"
        label={title}
      />
      <Select
        isLoading={isLoading}
        onChange={(value) => onProductChange(value)}
        options={productOptions}
        showSelectionAsValue={false}
        placeholder={placeholder}
        value={value}
      />
    </div>
  );
};

ManagerDashboardProductSection.displayName = 'ManagerDashboardProductSection';

export default ManagerDashboardProductSection;
